import styled from '@emotion/styled';
import { Tooltip, Typography } from '@material-ui/core';
import { Help } from '@material-ui/icons';
import { ClassAttributes, createElement, useContext } from 'react';
import { FormattedMessage } from 'react-intl';

import {
  CreativeFormat,
  CreativeFormatAspectRatio,
  CreativeFormatFastStart,
  CreativeFormatPlaybackTime,
} from '../../../graphql/generated/graphql';
import theme from '../../../theme';
import SubmissionForm from '../../component/compound/CreationDetail/SubmissionForm';
import { useCreateDeliverable } from '../../component/compound/CreationDetail/SubmissionForm/useCreateDeliverable';
import { SPACING_REGULAR, SPACING_UNIT } from '../../constant/spacing';
import { context as StateContext } from '../../context/state';
import { flexibleLayoutContainer } from '../../styleUtility/flexibleLayout';

interface Props {
  assignmentId: string;
  creativeFormat: Pick<CreativeFormat, 'id'> & {
    creativeType: Pick<CreativeFormat['creativeType'], 'key' | 'name'>;
    attribute: {
      playbackTime?: {
        seconds: CreativeFormatPlaybackTime['seconds'];
      } | null;
      aspectRatio?: Pick<CreativeFormatAspectRatio, 'aspectRatio' | 'size'> | null;
      fastStart?: CreativeFormatFastStart | null;
    };
  };
  onCreated: (creationId: string) => void;
  className?: string;
}

const SubmissionContent = ({
  assignmentId,
  creativeFormat,
  onCreated,
  className,
}: ClassAttributes<HTMLElement> & Props) => {
  const state = useContext(StateContext);
  const { createDeliverable, creating } = useCreateDeliverable(assignmentId, creativeFormat.id);
  const _onCreated = (param: string) => {
    onCreated(param);
  };

  return (
    <div className={className}>
      <TitleBar>
        <Title variant="h6">
          <FormattedMessage id="submission.title" /> #1
        </Title>
        {state.language == 'ja-JP' && (
          <FormattedMessage id="submission.help">
            {(text: string) => (
              <Tooltip placement="top" title={text}>
                <HelpAnchor
                  href="https://help.kaizen-ad.com/ja/articles/5590559-納品物投稿時の対応ファイル種別"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Help />
                </HelpAnchor>
              </Tooltip>
            )}
          </FormattedMessage>
        )}
      </TitleBar>
      <SubmissionForm
        creativeFormat={creativeFormat}
        canceled={false}
        hasDeliverables={false}
        submit={createDeliverable}
        submitting={creating}
        type="submission"
        onSubmit={_onCreated}
      />
    </div>
  );
};

const TitleBar = styled('div')({
  ...flexibleLayoutContainer({ alignItems: 'center' }),
  marginBottom: SPACING_UNIT * 3,
});

const Title = styled(Typography)({ marginRight: SPACING_REGULAR });

const UpdateDateTime = styled(Typography)({
  marginLeft: SPACING_REGULAR,
  marginRight: SPACING_REGULAR,
  lineHeight: 1,
});
UpdateDateTime.defaultProps = { variant: 'caption' };

const HelpAnchor = styled('a')({
  display: 'inline-flex',
  alignItems: 'center',
  color: theme.palette.grey['500'],
  textDecoration: 'none',
});

export default SubmissionContent;
